import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Radar, Briefcase, UserRound } from "lucide-react";

const GoogleBtn = ({ label }) => {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full" data-testid="google-auth-btn" onClick={handle}>
      <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
      {label}
    </Button>
  );
};

export default function Register() {
  const { t } = useLang();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "freelancer" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/register", form);
      loginWithToken(res.data.token, res.data.user);
      toast.success(t("register_title"));
      navigate(res.data.user.role === "client" ? "/my-requests" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const roles = [
    { id: "freelancer", label: t("role_freelancer"), icon: Briefcase },
    { id: "client", label: t("role_client"), icon: UserRound },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="grid place-items-center h-12 w-12 rounded-xl bg-primary text-primary-foreground mx-auto mb-4">
            <Radar className="h-6 w-6" />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("register_title")}</h1>
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-sm">
          <GoogleBtn label={t("continue_google")} />
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase">{t("or")}</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("role")}</Label>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                {roles.map((r) => (
                  <button key={r.id} type="button" data-testid={`role-${r.id}-btn`}
                    onClick={() => setForm({ ...form, role: r.id })}
                    className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-all ${
                      form.role === r.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:bg-secondary"
                    }`}>
                    <r.icon className={`h-5 w-5 ${form.role === r.id ? "text-primary" : "text-muted-foreground"}`} />
                    <span className="text-sm font-medium">{r.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="name">{t("name")}</Label>
              <Input id="name" required data-testid="register-name-input"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" type="email" required data-testid="register-email-input"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" type="password" required minLength={6} data-testid="register-password-input"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-btn">
              {busy ? t("processing") : t("register")}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground text-center mt-6">
            {t("have_account")} <Link to="/login" className="text-primary font-medium" data-testid="to-login-link">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
